"use client";

import { useMemo, useRef } from "react";
import { extractTime, parseIranPlate, toJalali } from "@/lib";
import CarLicenseImg from "@public/ir-flag.webp";
import irImg from "@public/ir.webp";
import { IRawPlateInquiryMutationResDataBody } from "@/interfaces/inquiries/vehicle";

interface IProps {
  plateData: IRawPlateInquiryMutationResDataBody;
  isOpen?: boolean;
  plateInput: { national_code: string };
}

const DetailPlateInquiryPdf = ({
  plateData,
  isOpen = true,
  plateInput,
}: IProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { convertedPlateNum, plateId, detachDate, plateNum } = plateData;
  const { two, letter, three, region } = parseIranPlate(
    convertedPlateNum ?? "",
  );
  const plateSerial = two;
  const plateLetters = letter;
  const plateNumbers = three;
  const plateRegion = region;
  const inactiveDate = `${toJalali(detachDate)} - ${extractTime(detachDate)}`;
  const contentMaxHeight = useMemo(() => {
    if (!contentRef.current) return 0;
    return isOpen ? contentRef.current.scrollHeight : 0;
  }, [isOpen]);

  const isInactive = !!detachDate;

  return (
    <div
      style={{
        padding: "0.5rem 1rem",
        direction: "ltr",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          transition: "all 0.3s duration-300",
          userSelect: "none",
        }}
      >
        {/* Status indicator - now part of the flow */}
        <div
          style={{
            display: "flex",
            width: "48px",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px 0 0 8px",
            border: "4px solid black",
            borderRight: "0",
            backgroundColor: isInactive ? "#fecaca" : "#dcfce7",
            color: isInactive ? "#991b1b" : "#166534",
          }}
        >
          <span
            style={{
              transform: "rotate(-90deg)",
              fontSize: "18px",
              fontWeight: "600",
              whiteSpace: "nowrap",
              lineHeight: 1,
              marginRight: "10px",
            }}
          >
            {isInactive ? "فک‌شده" : "فعال"}
          </span>
        </div>

        {/* License Plate */}
        <div
          style={{
            position: "relative",
            flex: 1,
            overflow: "hidden",
            borderRadius: "8px",
            borderTopLeftRadius: "0",
            borderBottomLeftRadius: "0",
            border: "4px solid black",
            backgroundColor: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "stretch" }}>
            {/* Left Blue Section with Flag and I.R. IRAN */}
            <div
              style={{
                display: "flex",
                width: "30px",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#003499",
                color: "white",
                paddingBottom: "15px",
              }}
            >
              <img
                src={CarLicenseImg.src}
                alt="شماره پلاک"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                }}
              />
            </div>

            {/* Main Numbers Section */}
            <div
              style={{
                direction: "rtl",
                position: "relative",
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "white",
                paddingBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  padding: "0 1rem",
                  fontWeight: "bold",
                  color: "black",
                }}
              >
                <span
                  style={{
                    minWidth: "40px",
                    textAlign: "center",
                    fontSize: "50px",
                    fontWeight: "bold",
                  }}
                >
                  {plateSerial}
                </span>
                <span
                  style={{
                    minWidth: "20px",
                    textAlign: "center",
                    fontSize: "50px",
                    fontWeight: "bold",
                    marginBottom: "12px",
                  }}
                >
                  {plateLetters}
                </span>
                <span
                  style={{
                    minWidth: "30px",
                    textAlign: "center",
                    fontSize: "50px",
                    fontWeight: "bold",
                  }}
                >
                  {plateNumbers}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                width: "100px",
                flexDirection: "column",
                alignItems: "center",
                borderLeft: "4px solid black",
                backgroundColor: "white",
                padding: "4px",
                paddingBottom: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={irImg.src}
                  alt="شماره پلاک"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: "40px",
                  fontWeight: "bold",
                  color: "black",
                }}
              >
                {plateRegion}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section - Full Width with seamless connection */}
      <div
        ref={contentRef}
        style={{
          marginTop: "-8px",
          overflow: "hidden",
          borderRadius: "0 0 8px 8px",
          border: "4px solid black",
          borderTop: "0",
          backgroundColor: "black",
          color: "white",
          transition: "all 0.3s duration-300",
          maxHeight: isOpen ? "auto" : 0,
        }}
      >
        <div
          style={{ position: "relative", padding: "12px", paddingTop: "16px" }}
        >
          {/* Single continuous vertical divider */}
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              width: "1px",
              transform: "translateX(-50%)",
              background:
                "linear-gradient(to bottom, rgba(243, 244, 246, 0.2), white, rgba(243, 244, 246, 0.2))",
            }}
          ></div>

          {/* First Row */}
          <div
            style={{
              marginBottom: "4px",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 16px 4px 0",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {plateNum}
              </span>
              <span style={{ fontSize: "12px", color: "#d1d5db" }}>
                شماره پلاک
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0 4px 16px",
              }}
            >
              <span style={{ fontSize: "12px", color: "#d1d5db" }}>کد ملی</span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {plateInput.national_code}
              </span>
            </div>
          </div>

          {/* Horizontal divider with gradient */}
          <div
            style={{
              margin: "0",
              height: "1px",
              background:
                "linear-gradient(to right, rgba(243, 244, 246, 0.2), white, rgba(243, 244, 246, 0.2))",
            }}
          ></div>

          {/* Second Row */}
          <div
            style={{ marginTop: "4px", display: "flex", alignItems: "stretch" }}
          >
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 16px 4px 0",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "white",
                }}
              >
                {plateId}
              </span>
              <span style={{ fontSize: "12px", color: "#d1d5db" }}>شناسه</span>
            </div>
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0 4px 16px",
              }}
            >
              {detachDate ? (
                <>
                  <span style={{ fontSize: "12px", color: "#d1d5db" }}>
                    تاریخ فک پلاک
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                    }}
                  >
                    {inactiveDate}
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: "12px", color: "#d1d5db" }}>
                    وضعیت
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                    }}
                  >
                    فعال
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPlateInquiryPdf;
